import i18n from '../i18n/config';
import type { BlogId } from '../constants/blogRoutes';
import Header from './Header';
import Footer from './Footer';

type Locale = 'en' | 'lv' | 'ru';

const ALLOWED_IDS: BlogId[] = [
  'independent_broker',
  'car_leasing',
  'home_credit',
  'business_loan',
  'consumer_loan',
  'credit_history',
  'rid_of_credits',
  'best_consumer_loan',
  'credit_limit'
];

const isBlogId = (value: string): value is BlogId =>
  ALLOWED_IDS.includes(value as BlogId);

const getCurrentLocale = (): Locale => {
  const locale = (i18n.locale || document.documentElement.lang || 'lv') as Locale;
  return ['en', 'lv', 'ru'].includes(locale) ? locale : 'lv';
};

interface BlogArticlePageProps {
  blogId: BlogId;
}

const BlogArticlePage = ({ blogId }: BlogArticlePageProps) => {
  const locale = getCurrentLocale();
  const title = i18n.t(`blogs.${blogId}.title`) as string;
  const heading = i18n.t(`blogs.${blogId}.content.heading`) as string;
  const imageName = i18n.t(`blogs.${blogId}.image_name`) as string;
  const rawParagraphs = i18n.t(`blogs.${blogId}.content.paragraphs`) as unknown;
  const paragraphs = Array.isArray(rawParagraphs)
    ? (rawParagraphs as string[])
    : [];
  const backLabel = i18n.t('meta.blog_back_to_index') as string;
  const indexHref = `/${locale}/blogs`;

  return (
    <div className="min-h-screen flex flex-col">
      <Header currentLanguage={i18n.locale} />

      <main className="flex-grow">
        <section
          className="relative overflow-hidden bg-gradient-to-br from-[#2c3e50] to-[#3498db] py-12 text-white sm:py-16"
          aria-labelledby="blog-article-heading"
        >
          <div className="absolute inset-0 bg-[url('/pattern.png')] opacity-20" />
          <div className="container relative z-10 mx-auto max-w-4xl px-4">
            <p className="mb-4">
              <a
                href={indexHref}
                className="text-sm font-medium text-white/90 underline-offset-4 transition-colors hover:text-white hover:underline"
              >
                {backLabel}
              </a>
            </p>
            <h1 id="blog-article-heading" className="text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
              {title}
            </h1>
          </div>
        </section>

        <article className="bg-gradient-to-br from-white to-[#f9fafb] py-10 sm:py-14">
          <div className="container mx-auto max-w-3xl px-4">
            <img
              src={`/images/blogs/${imageName}`}
              alt={title}
              className="mb-8 w-full rounded-lg object-cover shadow-md aspect-[16/9] sm:aspect-[2/1]"
            />
            <h2 className="text-xl font-semibold text-[#2c3e50] sm:text-2xl">{heading}</h2>
            <div className="prose prose-slate mt-6 max-w-none">
              {paragraphs.map((text, index) => (
                <p
                  key={index}
                  className="mb-4 whitespace-pre-line text-base leading-relaxed text-[#2c3e50]/90 last:mb-0"
                >
                  {text}
                </p>
              ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export const mountBlogArticlePage = (root: HTMLElement) => {
  const raw = root.dataset.blogId || '';
  if (!isBlogId(raw)) return;
  return <BlogArticlePage blogId={raw} />;
};

export default BlogArticlePage;
